import React, { useContext } from 'react';
//@ts-ignore
import * as style from './CountrySelectOverlay.module.css';
import RichText from '../Global/RichText';
import CtaLink from '../Global/CtaLink';
import { graphql } from 'gatsby';
import Dropdown, { Option } from 'react-dropdown';
import SvgDownArrowSm from '../Global/ShowMoreArrow/SvgDownArrowSm';
import Modal from 'react-modal';
import {
    KontentStringValue,
    KontentRichText,
} from '../../../types/KontentProps';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import CloseButton from '../Global/CloseButton';
import { navigate } from 'gatsby-link';
import {
    supportedRegionCodes,
    ukRegionIpCodes,
    malaysianRegionIpCodes,
} from '../../global/countries';
import { normalizeCountry, normalizeLocale } from '../../utils/locale';
import { markGeoRegionChosen } from '../../utils/geoRegion';
import { getIpLocation } from '../../utils/ipLocation';

interface CountryFlagMedia {
    id: string;
    elements: {
        file?: { value: { url: string }[] };
        icon_svg_file?: { value: { url: string }[] };
    };
}

interface country {
    elements: {
        country: KontentStringValue;
        flag: { value: CountryFlagMedia[] };
        language: KontentStringValue;
        site_url: KontentStringValue;
        domain_url: KontentStringValue;
    };
}

interface CountryDetail {
    country: string;
    language: string;
    flagUrl?: string;
    siteUrl: string;
    domainUrl: string;
}

interface IpDetailsResponse {
    country_code: string;
}

export interface CountrySelectProps {
    kontentItemId?: string;
    disclaimer_copy: KontentRichText;
    headline: KontentStringValue;
    introduction_copy: KontentRichText;
    cta_label: KontentStringValue;
    countries_for_selection: { value: country[] };
    navFlag?: Boolean;
}

type countryCodetype = (typeof supportedRegionCodes)[number] | 'us-en';

const CountrySelectOverlay: React.FC<CountrySelectProps> = ({
    kontentItemId,
    disclaimer_copy,
    headline,
    cta_label,
    introduction_copy,
    countries_for_selection,
    navFlag,
}) => {
    const pageMetadata = useContext(PageMetadataContext);
    const [ipDetails, setIpDetails] = React.useState<IpDetailsResponse | null>(
        null
    );
    const [isActive, setActive] = React.useState(false);
    const navFlagRef = React.useRef<HTMLDivElement>(null);
    const [countryLanguages, setLan] = React.useState<string | undefined>();
    const [flagUrl, setFlag] = React.useState<string | undefined>();
    const [dropdownUrl, setDropdownUrl] = React.useState('');
    const [isCountrySelect, setSelectCountry] = React.useState(false);
    const [isLanSelect, setSelectLang] = React.useState(false);
    const [currentCountry, setCurrentCountry] = React.useState<string | null>(
        null
    );
    const [currentCountryLang, setCurrentCountryLang] = React.useState<
        string | null
    >(null);

    const splittedCountryDomainPath =
        typeof window !== 'undefined'
            ? window.location.origin.split('.').at(-1)
            : '';
    const currentDomainCountry =
        splittedCountryDomainPath != 'com' ? splittedCountryDomainPath : '';

    const getDefaultPreferedLanguage = (path: string) => {
        if (path == 'nz') return 'nz-en';
        if (path == 'au') return 'au-en';
        return 'us-en';
    };

    const preferredLanguageRaw =
        pageMetadata?.preferredLanguage === 'default'
            ? getDefaultPreferedLanguage(splittedCountryDomainPath || '')
            : pageMetadata?.preferredLanguage;

    const preferredLanguage = normalizeLocale(preferredLanguageRaw || 'us-en');

    React.useEffect(() => {
        getIpLocation().then((data) => {
            if (data) setIpDetails(data as IpDetailsResponse);
        });
    }, []);

    const isVercelOrLocalDomain = () => {
        if (typeof window === 'undefined') return false;
        const isAutoGeneratedDomain = ['vercel.app', 'localhost'].filter(
            (item) => window.location.origin.includes(item)
        );
        return isAutoGeneratedDomain.length > 0;
    };

    const currentPagePath =
        pageMetadata?.slug && pageMetadata?.pagePaths?.[pageMetadata.slug];
    const countryTrees = countries_for_selection?.value;

    const handleToggle = () => {
        setActive(!isActive);
    };

    React.useEffect(() => {
        const el = navFlagRef.current;
        if (!el || !navFlag) return;
        const handler = (e: MouseEvent) => {
            e.stopPropagation();
            setActive((prev) => !prev);
        };
        el.addEventListener('click', handler);
        return () => el.removeEventListener('click', handler);
    }, [navFlag]);

    const dropdownCountryOptions: string[] = [];
    const language: string[] = countryLanguages ? [countryLanguages] : [];

    const countryDetails: Record<string, CountryDetail> = {};

    const getCountryUrlCode = (url: string): countryCodetype => {
        const splitValues: string[] = url ? url.split('/') : [];
        const lastSegment = splitValues[splitValues.length - 1];
        const splitByDots: string = url
            ? url.split('.')[url.split('.').length - 1].replace('/', '')
            : '';
        let countryCode: string = lastSegment
            ? lastSegment
            : splitByDots !== 'com'
            ? splitByDots + '-en'
            : 'us-en';
        return normalizeLocale(countryCode) as countryCodetype;
    };

    const getKeyForSiteUrl = (siteUrl: string) => {
        const code = getCountryUrlCode(siteUrl || '');
        return normalizeLocale(code);
    };

    countryTrees?.forEach((item) => {
        dropdownCountryOptions.push(item.elements.country.value);

        const rawSiteUrl = item.elements.site_url?.value || '';
        const countryUrlCode = getCountryUrlCode(rawSiteUrl);

        let imageFlag =
            item.elements.flag.value[0]?.elements?.file?.value[0]?.url;
        let svgFlagIcon =
            item.elements.flag.value[0]?.elements?.icon_svg_file?.value[0]?.url;
        let setFlagUrl = item.elements.flag?.value.length
            ? imageFlag
                ? imageFlag
                : svgFlagIcon
            : '';

        let domainUrl =
            typeof window !== 'undefined'
                ? isVercelOrLocalDomain()
                    ? window?.location.origin
                    : item.elements.domain_url.value.replace(
                          'sunlighten',
                          window?.location.host.split('sunlighten')[0] +
                              'sunlighten'
                      )
                : item.elements.domain_url.value;

        const key = getKeyForSiteUrl(rawSiteUrl);

        countryDetails[key] = {
            country: item.elements.country.value,
            language: item.elements.language.value,
            flagUrl: setFlagUrl,
            siteUrl: rawSiteUrl,
            domainUrl: domainUrl,
        };
    });

    let otherCountryCode: string | null = null;
    if (ipDetails) {
        otherCountryCode = currentDomainCountry
            ? normalizeCountry(currentDomainCountry)
            : normalizeCountry(ipDetails.country_code);
    }

    let domainCountryPath = preferredLanguage;

    const defaultCountryOptions = domainCountryPath
        ? countryDetails[normalizeLocale(domainCountryPath)]?.country
        : '';
    const [defaultLaungauageOptions, setDefaultLaungauageOptions] =
        React.useState<string | undefined>(
            domainCountryPath
                ? countryDetails[normalizeLocale(domainCountryPath)]?.language
                : undefined
        );
    const defaultFlagUrl = domainCountryPath
        ? countryDetails[normalizeLocale(domainCountryPath)]?.flagUrl
        : '';
    let domainUrl = domainCountryPath
        ? countryDetails[normalizeLocale(domainCountryPath)]?.domainUrl
        : '';

    if (!currentDomainCountry) {
        if (
            (ukRegionIpCodes as readonly string[]).includes(
                normalizeCountry(otherCountryCode || '')
            )
        ) {
            domainCountryPath = 'gb-en';
        } else if (
            (malaysianRegionIpCodes as readonly string[]).includes(
                normalizeCountry(otherCountryCode || '')
            )
        ) {
            domainCountryPath = 'my-en';
        }
    }

    const applyQueryParamRegion = () => {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        const paramsCountrtyCode = params.get('cc');
        if (paramsCountrtyCode) {
            markGeoRegionChosen(String(paramsCountrtyCode).toLowerCase());
            try {
                const url = new URL(window.location.href);
                url.searchParams.delete('cc');
                window.history.replaceState({}, document.title, url.toString());
            } catch {
                // ignore
            }
        }
    };

    const checkPrivacyPolicy = () => {
        if (typeof window === 'undefined') return '';
        const lastSegment = window.location.pathname
            .split('/')
            .slice(-1)[0]
            .toLowerCase();
        return lastSegment === 'privacy-policy' ? '/privacy-policy' : '';
    };

    React.useEffect(() => {
        applyQueryParamRegion();
    }, [ipDetails]);

    const pageCountry =
        preferredLanguage &&
        countryDetails[normalizeLocale(preferredLanguage)]?.country;
    const pageLanguage =
        preferredLanguage &&
        countryDetails[normalizeLocale(preferredLanguage)]?.language;
    const pageFlag =
        preferredLanguage &&
        countryDetails[normalizeLocale(preferredLanguage)]?.flagUrl;

    const onChange = (event: Option, selectedValue: string) => {
        if (selectedValue == 'language') {
            setSelectLang(true);
            setCurrentCountryLang(event?.value);
            if (currentCountry == null && preferredLanguage) {
                setCurrentCountry(
                    countryDetails[normalizeLocale(preferredLanguage)]?.country
                );
            }
            return;
        }

        const SelectedCountry = event.value;
        countryTrees?.forEach((country_data: country) => {
            const { country, language, flag, site_url } = country_data.elements;
            if (country.value == SelectedCountry) {
                let imageFlag = flag.value[0]?.elements?.file?.value[0]?.url;
                let svgFlagIcon =
                    flag.value[0]?.elements?.icon_svg_file?.value[0]?.url;
                let isFlag = imageFlag ? imageFlag : svgFlagIcon;
                const lan = language?.value;
                const flagUrlVal = flag.value.length ? isFlag : '';

                const countryObjKey = getKeyForSiteUrl(site_url?.value || '');
                const countryObj = countryDetails[countryObjKey];

                const fallbackCountryObj = Object.values(countryDetails).filter(
                    (countryDetail: CountryDetail) =>
                        countryDetail.country.toLowerCase() ==
                            country.value.toLowerCase() &&
                        countryDetail.language.toLowerCase() ==
                            lan.toLowerCase()
                )[0];

                const finalCountryObj = countryObj || fallbackCountryObj;
                let linkURL = site_url?.value;

                if (finalCountryObj) {
                    if (
                        window.location.origin === finalCountryObj?.domainUrl ||
                        isVercelOrLocalDomain()
                    ) {
                        if (countrySiteUrl(finalCountryObj?.siteUrl)) {
                            setDropdownUrl(finalCountryObj?.domainUrl);
                        } else if (isVercelOrLocalDomain()) {
                            const linkKey = getKeyForSiteUrl(linkURL || '');
                            const resolvedDomain =
                                countryDetails[linkKey]?.domainUrl ||
                                finalCountryObj?.domainUrl ||
                                '';
                            setDropdownUrl(resolvedDomain + '/' + linkURL);
                        } else {
                            setDropdownUrl(
                                finalCountryObj?.domainUrl + '/' + linkURL
                            );
                        }
                    } else {
                        const navigatingUrlCountryCode = countrySiteUrl(
                            finalCountryObj?.siteUrl
                        )
                            ? ''
                            : `/${finalCountryObj.siteUrl}`;
                        let selectedCountryCode =
                            finalCountryObj.siteUrl == 'https://sunlighten.com/'
                                ? 'us-en'
                                : finalCountryObj.siteUrl;
                        setDropdownUrl(
                            finalCountryObj?.domainUrl +
                                navigatingUrlCountryCode +
                                '?cc=' +
                                selectedCountryCode
                        );
                    }

                    setCurrentCountryLang(lan);
                    setCurrentCountry(country?.value);
                    setDefaultLaungauageOptions(lan);
                    setLan(lan);
                    setFlag(flagUrlVal);
                    setSelectCountry(true);
                }
            }
        });
    };

    const countrySiteUrl = (siteUrl: string) => {
        const siteUrlCountry = ['https://sunlighten.com/', 'au-en', 'nz-en'];
        return siteUrlCountry.includes(siteUrl);
    };

    const computeDropdownUrlForRegion = (regionCode: string): string => {
        const detail = countryDetails[normalizeLocale(regionCode)];
        if (!detail || typeof window === 'undefined') return '';
        if (
            window.location.origin === detail.domainUrl ||
            isVercelOrLocalDomain()
        ) {
            if (countrySiteUrl(detail.siteUrl)) {
                return detail.domainUrl;
            } else if (isVercelOrLocalDomain()) {
                return (detail.domainUrl || '') + '/' + detail.siteUrl;
            } else {
                return detail.domainUrl + '/' + detail.siteUrl;
            }
        }
        const pathSegment = countrySiteUrl(detail.siteUrl)
            ? ''
            : `/${detail.siteUrl}`;
        const cc =
            detail.siteUrl === 'https://sunlighten.com/'
                ? 'us-en'
                : detail.siteUrl;
        return detail.domainUrl + pathSegment + '?cc=' + cc;
    };

    React.useEffect(() => {
        if (!dropdownUrl && preferredLanguage) {
            const url = computeDropdownUrlForRegion(preferredLanguage);
            if (url) setDropdownUrl(url);
        }
    }, [preferredLanguage]);

    const saveCountryCodeSelection = () => {
        const effectiveCountry = currentCountry || pageCountry || '';
        const effectiveLang = currentCountryLang || pageLanguage || '';

        const match = Object.entries(countryDetails).find(
            ([, detail]) =>
                detail.country.toLowerCase() ===
                    effectiveCountry.toLowerCase() &&
                detail.language.toLowerCase() === effectiveLang.toLowerCase()
        );

        if (match) {
            const [regionCode] = match;
            markGeoRegionChosen(regionCode);
        }

        setActive(false);
    };

    const closeModal = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        setFlag(pageFlag);
        setDefaultLaungauageOptions(pageLanguage);
        setCurrentCountry(null);
        setCurrentCountryLang(null);
        setActive(false);
    };

    const doc = typeof document !== 'undefined' ? document.body : undefined;
    return (
        <>
            <div
                ref={navFlagRef}
                id="navigation__Country-flag"
                data-kontent-item-id={kontentItemId}
            >
                {navFlag ? (
                    <div>
                        <img
                            src={defaultFlagUrl}
                            alt="Country flag"
                            className={style.countryFlag}
                            width={24}
                            height={24}
                            decoding="async"
                        />
                    </div>
                ) : (
                    <div>
                        {pageCountry}-{pageLanguage}
                    </div>
                )}
            </div>

            <Modal
                isOpen={isActive}
                contentLabel="Country Select Overlay"
                onRequestClose={closeModal}
                overlayClassName={style.overlayFooter}
                appElement={doc}
            >
                <div className={style.closeButton}>
                    <CloseButton visualStyle="orange" onClick={closeModal} />
                </div>
                <div>
                    <div className={style.container}>
                        <div
                            className={style.head}
                            data-kontent-element-codename="headline"
                        >
                            {headline?.value}
                        </div>

                        <div
                            className={style.subContiner}
                            style={{ marginTop: flagUrl ? '2%' : '1%' }}
                        >
                            <div
                                className={style.flagWrapper}
                                data-kontent-element-codename="flag"
                            >
                                <img
                                    src={flagUrl ? flagUrl : defaultFlagUrl}
                                    alt="Select your country"
                                    width={40}
                                    height={40}
                                    decoding="async"
                                />
                            </div>
                            <div className={style.intro}>
                                {RichText(
                                    introduction_copy,
                                    false,
                                    undefined,
                                    undefined,
                                    'introduction_copy'
                                )}
                            </div>
                        </div>

                        <div className={style.form}>
                            <form>
                                <div className={style.selectsWrapper}>
                                    <div
                                        className={style.selectWrapper}
                                        role="group"
                                        aria-label="Select a location"
                                    >
                                        <Dropdown
                                            menuClassName={style.menuClass}
                                            placeholderClassName={
                                                style.myPlaceholderClassName
                                            }
                                            options={dropdownCountryOptions}
                                            value={defaultCountryOptions}
                                            onChange={(event) =>
                                                onChange(event, 'country')
                                            }
                                            controlClassName={
                                                isCountrySelect
                                                    ? style.selected
                                                    : style.nonSelected
                                            }
                                            arrowClassName="myArrowClassName"
                                            arrowClosed={
                                                <span className="arrow-closed">
                                                    <div
                                                        className={
                                                            style.downArrow
                                                        }
                                                    >
                                                        <SvgDownArrowSm
                                                            strokeColor="grey"
                                                            transform="180deg"
                                                        />
                                                    </div>
                                                </span>
                                            }
                                            arrowOpen={
                                                <span className="arrow-open">
                                                    <div
                                                        className={
                                                            style.upArrow
                                                        }
                                                    >
                                                        <SvgDownArrowSm
                                                            strokeColor="grey"
                                                            transform="0deg"
                                                        />
                                                    </div>
                                                </span>
                                            }
                                            placeholder="SELECT A LOCATION"
                                        />
                                    </div>
                                    <div
                                        className={style.selectWrapper}
                                        role="group"
                                        aria-label="Select a language"
                                    >
                                        <Dropdown
                                            options={language}
                                            value={defaultLaungauageOptions}
                                            onChange={(event) =>
                                                onChange(event, 'language')
                                            }
                                            controlClassName={
                                                isLanSelect
                                                    ? style.selected
                                                    : style.nonSelected
                                            }
                                            menuClassName={style.menuClass}
                                            placeholderClassName={
                                                style.myPlaceholderClassName
                                            }
                                            placeholder="SELECT A LANGUAGE"
                                            arrowClassName="myArrowClassName"
                                            arrowClosed={
                                                <span className="arrow-closed">
                                                    <div
                                                        className={
                                                            style.downArrow
                                                        }
                                                    >
                                                        <SvgDownArrowSm
                                                            strokeColor="grey"
                                                            transform="180deg"
                                                        />
                                                    </div>
                                                </span>
                                            }
                                            arrowOpen={
                                                <span className="arrow-open">
                                                    <div
                                                        className={
                                                            style.upArrow
                                                        }
                                                    >
                                                        <SvgDownArrowSm
                                                            strokeColor="grey"
                                                            transform="0deg"
                                                        />
                                                    </div>
                                                </span>
                                            }
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>
                        <div className={style.intro}>
                            {RichText(
                                disclaimer_copy,
                                false,
                                undefined,
                                undefined,
                                'disclaimer_copy'
                            )}
                        </div>
                        <div
                            onClickCapture={(e) => {
                                // Prevent the Kontent.ai Smart Link SDK from
                                // intercepting this click. KSL attaches a
                                // native bubble-phase listener to the <a>
                                // (data-kontent-element-codename="cta_label")
                                // that calls preventDefault()+stopPropagation().
                                // React's onClickCapture fires at the React
                                // root level (before native listeners on child
                                // DOM elements), so stopping the native event
                                // here blocks KSL while still allowing us to
                                // navigate manually.
                                e.nativeEvent.stopImmediatePropagation();
                                saveCountryCodeSelection();
                                if (dropdownUrl) {
                                    window.location.href = dropdownUrl;
                                }
                            }}
                            className={style.buttonWrapper}
                        >
                            <CtaLink
                                href={dropdownUrl}
                                visualStyle="outlineLight"
                                elementCodename="cta_label"
                            >
                                {cta_label?.value}
                            </CtaLink>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default CountrySelectOverlay;

export const query = graphql`
    fragment CountrySelectOverlay on kontent_item_component___country_select_overlay {
        elements {
            countries_for_selection {
                value {
                    ... on kontent_item_content_item___country_sites {
                        id
                        elements {
                            country {
                                value
                            }
                            flag {
                                value {
                                    ... on kontent_item_media___icon {
                                        id
                                        elements {
                                            icon_svg_file {
                                                value {
                                                    url
                                                }
                                            }
                                        }
                                    }
                                    ... on kontent_item_media___image {
                                        id
                                        elements {
                                            file {
                                                value {
                                                    url
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            language {
                                value
                            }
                            site_url {
                                value
                            }
                            domain_url {
                                value
                            }
                        }
                    }
                }
            }
            cta_label {
                value
            }
            disclaimer_copy {
                value
            }
            headline {
                value
            }
            introduction_copy {
                value
            }
        }
    }
`;
